/** PATCH helper for the region editor. Region has no `updated_ts` — writes are
 *  last-writer-wins, so there is no optimistic-concurrency token and no 409
 *  flow (unlike tours / trip types). */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ConsoleApiError } from '@/api/client';
import * as regionsApi from '@/api/regions';
import type { RegionDetail, RegionWriteInput } from '@/api/regions';
import { useSnackbar } from '@/components/Snackbar';

export function useRegionPatch(region: RegionDetail, onSaved: () => void) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: (body: RegionWriteInput) => regionsApi.update(region.id, body),
    onSuccess: () => {
      snackbar.show('Saved.', 'success');
      qc.invalidateQueries({ queryKey: ['regions'] });
      onSaved();
    },
    onError: (e) => {
      const msg = e instanceof ConsoleApiError ? e.message : 'Save failed.';
      snackbar.show(msg, 'error');
    },
  });

  return {
    save: (body: RegionWriteInput) => mutation.mutate(body),
    saving: mutation.isPending,
    /** Field-keyed server errors from a 400, if any. */
    fieldErrors:
      mutation.error instanceof ConsoleApiError && mutation.error.status === 400
        ? (mutation.error.body as Record<string, unknown>)
        : null,
  };
}
